import { memo, forwardRef, useCallback, useMemo } from 'react';
import { FormHelperText, Box } from '@mui/material';
import { CheckboxComponentProps } from './Checkbox.types';
import { StyledCheckbox, StyledFormControlLabel } from './Checkbox.styles';

/**
 * Checkbox Component
 *
 * Professional checkbox component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Basic checkbox
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(e, checked) => setAccepted(checked)}
 * />
 *
 * // With validation
 * <Checkbox
 *   label="I agree"
 *   required
 *   error={!!errors.agreement}
 *   errorMessage={errors.agreement}
 * />
 *
 * // Indeterminate state
 * <Checkbox
 *   label="Select all"
 *   indeterminate={someSelected}
 *   checked={allSelected}
 * />
 * ```
 */
export const Checkbox = memo(
  forwardRef<HTMLButtonElement, CheckboxComponentProps>(function Checkbox(
    {
      size = 'md',
      label,
      helperText,
      validationState = 'default',
      error = false,
      errorMessage,
      checked,
      defaultChecked,
      indeterminate = false,
      disabled = false,
      required = false,
      onChange,
      value,
      name,
      id,
      className,
      'data-testid': dataTestId,
      labelPlacement = 'end',
      ...props
    },
    ref
  ) {
    // Determine validation state
    const finalValidationState = useMemo(() => {
      if (error || errorMessage) return 'error';
      return validationState;
    }, [error, errorMessage, validationState]);

    // Determine helper text
    const finalHelperText = useMemo(() => {
      if (errorMessage) return errorMessage;
      return helperText;
    }, [errorMessage, helperText]);

    // Memoize change handler
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, checkedValue: boolean) => {
        onChange?.(event, checkedValue);
      },
      [onChange]
    );

    // Create the checkbox element
    const checkboxElement = (
      <StyledCheckbox
        ref={ref}
        checked={checked}
        defaultChecked={defaultChecked}
        indeterminate={indeterminate}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        value={value}
        name={name}
        id={id}
        inputProps={
          {
            'data-testid': dataTestId,
            ...props.inputProps,
          } as Record<string, unknown>
        }
        {...props}
      />
    );

    // If there's a label, wrap with FormControlLabel
    const content = label ? (
      <StyledFormControlLabel
        checkboxSize={size}
        validationState={finalValidationState}
        control={checkboxElement}
        label={label}
        disabled={disabled}
        labelPlacement={labelPlacement}
        className={className}
      />
    ) : (
      checkboxElement
    );

    // If there's helper text, wrap in a box
    if (finalHelperText) {
      return (
        <Box>
          {content}
          <FormHelperText
            error={finalValidationState === 'error'}
            sx={{ ml: label && labelPlacement === 'end' ? '32px' : 0 }}
          >
            {finalHelperText}
          </FormHelperText>
        </Box>
      );
    }

    return content;
  })
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
